/**
 * InProgressCreatureCard Component
 *
 * Displays an in-progress creature generation in the gallery grid with:
 * - EmergenceOverlay showing base sprite (if available) with floating pixels
 * - Progress percentage
 * - Blocked click interaction until complete
 * - Status message
 */

import React from 'react';
import { EmergenceOverlay } from '../EmergenceOverlay';
import './InProgressCreatureCard.css';

export interface InProgressJob {
  jobId: string;
  progress: number;
  status: 'queued' | 'processing';
  partialAssets?: {
    baseSprite: string;
    textDescription?: string;
  };
  startedAt?: string;
  textDescription?: string;
}

export interface InProgressCreatureCardProps {
  job: InProgressJob;
  onClick?: () => void;
}

export const InProgressCreatureCard: React.FC<InProgressCreatureCardProps> = ({
  job,
  onClick
}) => {
  // Determine status message based on progress
  const getStatusMessage = (): string => {
    if (job.status === 'queued') {
      return 'Queued...';
    }
    if (job.progress < 20) {
      return 'Starting generation...';
    }
    if (job.progress < 40) {
      return 'Generating base...';
    }
    if (job.progress < 70) {
      return 'Creating animations...';
    }
    if (job.progress < 90) {
      return 'Finalizing...';
    }
    return 'Almost done...';
  };

  // Get description text for display
  const getDescriptionText = (): string | undefined => {
    return job.textDescription || job.partialAssets?.textDescription;
  };

  const description = getDescriptionText();

  // Block click handler - show tooltip or ignore
  const handleClick = () => {
    // Optionally call onClick to show a message
    onClick?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.();
    }
  };

  return (
    <div
      className="in-progress-creature-card"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`Creature generation in progress: ${job.progress}%`}
      aria-disabled="true"
    >
      {/* EmergenceOverlay in place of sprite image */}
      <div className="in-progress-creature-card__image-container">
        <EmergenceOverlay
          sprite={job.partialAssets?.baseSprite}
          progress={job.progress}
          statusMessage={getStatusMessage()}
          size="small"
          isComplete={false}
          className="in-progress-creature-card__overlay"
        />
      </div>

      {/* Content */}
      <div className="in-progress-creature-card__content">
        <h3 className="in-progress-creature-card__title">
          {job.status === 'queued' ? 'Queued' : 'Generating...'}
        </h3>

        {/* Description snippet if available */}
        {description && (
          <p className="in-progress-creature-card__description">
            {description.length > 40
              ? `${description.substring(0, 40)}...`
              : description}
          </p>
        )}

        {/* Job ID (truncated) */}
        <div className="in-progress-creature-card__metadata">
          <span className="in-progress-creature-card__job-id">
            ID: {job.jobId.substring(0, 8)}...
          </span>
        </div>
      </div>

      {/* Blocked overlay to prevent clicks */}
      <div className="in-progress-creature-card__blocked-overlay">
        <span className="in-progress-creature-card__blocked-text">
          Still generating...
        </span>
      </div>
    </div>
  );
};

export default InProgressCreatureCard;
